import { z } from 'zod';

import { ROLE_RANK, type WorkspaceRole } from './enums.js';

/**
 * KİRACI TARAFININ ORTAK SÖZLEŞMESİ — bütün alanlar bunu okur.
 *
 * `admin.ts` platform yöneticisinin sözleşmesiydi; bu dosya kiracının.
 * Sekiz alan (integrations, analytics, workspaces, billing, automation,
 * notifications, agent, creative) aynı sayfalama kabını, aynı hata zarfını,
 * aynı denetim sözlüğünü ve aynı workspace bağlamını kullanır. İki alan kendi
 * kabını yazarsa arayüz iki farklı davranış öğrenmek zorunda kalır.
 *
 * ---------------------------------------------------------------------------
 * Serileştirme — üç kural, istisnasız
 * ---------------------------------------------------------------------------
 *  1. Tarih  → ISO 8601 DİZE (`2026-08-10T09:00:00.000Z`). Prisma `Date`
 *     döndürür; tip tarafında `Date` demek frontend'i yalana zorluyor.
 *  2. Decimal → `.toNumber()` ile `number`. Ham bırakılırsa Fastify onu
 *     `{s,e,d}` nesnesine çevirir, arayüzde `NaN` görünür.
 *  3. BigInt  → `Number()`. `MetricDaily.impressions/clicks` BigInt ve
 *     `JSON.stringify(1n)` TypeError atar; ham dönen uç 500 verir.
 *     2^53 sınırı reklam gösterimi için fazlasıyla yeterli.
 *
 * Toplama işlemi HER ZAMAN veritabanında (`groupBy`/`aggregate`) yapılır;
 * satırları JS'e çekip float toplamak kuruş kaybettirir.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Workspace bağlamı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Etkin workspace nasıl taşınır.
 *
 * Öncelik: `X-Workspace-Id` başlığı → `mentorivo_ws` çerezi → 400.
 *
 * Başlığın öncelikli olması ŞART: aynı tarayıcıda iki sekmede iki farklı
 * workspace açık olabilir ve tek çerez ikisini de yanlış besler. Çerez yalnız
 * "son kullanılan" hafızası; ilk yüklemede ve sunucu bileşenlerinde işe yarar.
 *
 * Guard üyeliği DOĞRULAR. Üye olmayana **404** döner, 403 değil — 403 "bu
 * kimlikte bir workspace var" bilgisini sızdırır (admin panelinde verilen
 * kararın aynısı).
 */
export const WORKSPACE_HEADER = 'x-workspace-id';
export const WORKSPACE_COOKIE = 'mentorivo_ws';

/**
 * Para ya da platform durumu değiştiren her yazma ucu bu başlığı KABUL EDER.
 *
 * Sunucu yanıtı Redis'te 24 saat saklar; aynı anahtarla gelen ikinci istek
 * işi tekrar yapmaz, saklanan yanıtı döner. Ağ koptuğunda kullanıcının
 * "Öde" ya da "Bütçeyi artır" düğmesine ikinci kez basması normaldir —
 * bedelini iki kez ödemesi değil.
 */
export const IDEMPOTENCY_HEADER = 'idempotency-key';
export const IDEMPOTENCY_TTL_SECONDS = 24 * 60 * 60;

/// Guard'ın isteğe yazdığı bağlam. `@CurrentWorkspace()` bunu döndürür.
export interface WorkspaceContext {
  workspaceId: string;
  /// İstek sahibinin bu workspace'teki rolü.
  role: WorkspaceRole;
  membershipId: string;
  /// Sunum ve zamanlama için otorite (veri tarafının otoritesi hesap zaman
  /// dilimidir — `analytics.ts`'teki "gün sınırı" notuna bak).
  timezone: string;
  /// Workspace'in raporlama para birimi (ISO 4217).
  currency: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Sayfalama ve liste sorgusu
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Neden offset (admin ile aynı gerekçe): kullanıcının sorusu "kaç tane var" ve
 * "3. sayfaya git". İmleç bu iki soruyu cevaplayamıyor.
 *
 * TEK İSTİSNA kampanya hiyerarşisi: binlerce satır + sanal kaydırma. Orada
 * `cursor` de destekleniyor (`CursorPage`). İki biçim bilinçli, kazara değil —
 * hangi ucun hangisini kullandığı `analytics.ts`'te yazılı.
 */
export const PAGE_SIZE_DEFAULT = 25;
export const PAGE_SIZE_MAX = 200;

/**
 * 🔴 KULLANICININ VEREMEYECEĞİ AD ÖN EKİ.
 *
 * Sistem, kullanıcıdan gizlediği kayıtları `__` ön ekiyle işaretliyor
 * (bugün tek örnek: `__agent__`, ajan önerilerinin taşıyıcı kuralı) ve
 * listeler bu satırları filtreliyor.
 *
 * Kullanıcı kendi kaydına aynı adı verebilseydi onu kendi ekranından
 * gizlemiş olurdu — ve bir daha ne düzenleyebilir ne silebilirdi, çünkü
 * `requireWorkflow` o adı reddediyor. Kurtarma yolu olmadığı için kapı
 * girişte kapalı.
 *
 * Burada, `common.ts`te duruyor çünkü hem `automation.ts` (kural adı) hem
 * `agents.ts` (ajan adı) okuyor; birinde tanımlansaydı iki dosya birbirini
 * import ederdi.
 */
export const RESERVED_NAME_PREFIX = '__';

/// Sorgu dizesinden gelen boolean. `z.coerce.boolean()` KULLANILMAZ —
/// `"false"` boş olmayan bir dize olduğu için `true`ya çevrilir, sessiz hata.
export const boolParam = z.enum(['true', 'false']).transform((v) => v === 'true');

/**
 * Sorgu dizesinden gelen KİMLİK LİSTESİ — `?ids=a&ids=b`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden `z.array()` TEK BAŞINA yetmiyor
 * ═══════════════════════════════════════════════════════════════════════════
 * Fastify'ın sorgu ayrıştırıcısı tekrar eden anahtarı diziye çeviriyor ama
 * TEK bir `?ids=a` düz bir DİZE olarak geliyor. `z.array(z.uuid())` onu
 * reddeder — ve arıza tam olarak en sık durumda çıkar: tek kreatif seçen
 * kullanıcı.
 *
 * `boolParam` ile aynı sınıf bir tuzak: sorgu dizesi tipli değil ve şemanın
 * bunu bilmesi gerekiyor.
 */
export const uuidListParam = z
  .union([z.uuid(), z.array(z.uuid())])
  .transform((value) => (Array.isArray(value) ? value : [value]));

/// Virgülle ayrılmış çoklu değer (`?platforms=META,GOOGLE`). Boş öğeler atılır.
export const csvParam = z
  .string()
  .transform((v) =>
    v
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0),
  )
  .pipe(z.array(z.string()).max(100));

/// Arama metni. `?q=` (boş) "arama yok" demek, `contains ''` değil.
export const searchParam = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .optional()
  .transform((v) => (v && v.length > 0 ? v : undefined));

export const sortDirSchema = z.enum(['asc', 'desc']).default('desc');
export type SortDir = z.infer<typeof sortDirSchema>;

/// Tüm kiracı liste uçlarının ortak parçası.
export const listQuerySchema = z.object({
  q: searchParam,
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_DEFAULT),
});
export type ListQuery = z.infer<typeof listQuerySchema>;

/// Sayfalanmış yanıtın kabı. `pageCount` sunucuda hesaplanıyor ki arayüz
/// bölmeyi (ve sıfıra bölmeyi) tekrar düşünmesin.
export interface Page<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/// İmleçli kap — yalnızca kampanya hiyerarşisi ve execution log akışı.
/// `nextCursor === null` liste bitti demek. `total` opsiyonel: imleçli sorguda
/// toplam saymak pahalı, istenmediyse hesaplanmaz.
export interface CursorPage<T> {
  rows: T[];
  nextCursor: string | null;
  total?: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// Yanıt meta'sı ve veri tazeliği
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Her kiracı OKUMA yanıtı bunu taşır.
 *
 * `generatedAt` olmadan arayüz "şu kadar önce güncellendi" derken `Date.now()`
 * kullanır ve SSR/CSR arasında uyuşmazlık üretir (hidrasyon hatası).
 * `staleMinutes` sunucuda hesaplanır ki iki taraf aynı sayıyı görsün.
 */
export interface ResponseMeta {
  generatedAt: string;
  /// Kapsamdaki hesapların EN ESKİ `lastSyncedAt` değeri; hiç senkron
  /// yapılmadıysa null. "Son güncelleme: 14 dk önce" etiketinin kaynağı.
  lastSyncedAt: string | null;
  /// `generatedAt - lastSyncedAt`, dakika. `lastSyncedAt` null ise null.
  staleMinutes: number | null;
}

/// Analytics ve kampanya uçlarının taşıdığı genişletilmiş tazelik bilgisi.
export interface DataFreshness extends ResponseMeta {
  /// Şu anda senkronu süren hesaplar. Arayüz bunlar için "güncelleniyor"
  /// göstergesi basar; tabloyu kilitlemez.
  syncingAccountIds: string[];
}

/**
 * Boş durumun ÜÇ AYRI hâli — tek bir "boş" bayrağı üçünü de yanlış gösterir.
 *
 *  no_connection → hiç bağlı hesap yok      → onboarding'e yönlendir
 *  syncing       → bağlı ama ilk senkron sürüyor → bekleme ekranı
 *  no_data       → bağlı ve senkron bitti, seçili aralıkta satır yok
 *  ok            → veri var
 */
export const DataState = {
  NO_CONNECTION: 'no_connection',
  SYNCING: 'syncing',
  NO_DATA: 'no_data',
  OK: 'ok',
} as const;
export type DataState = (typeof DataState)[keyof typeof DataState];

// ═══════════════════════════════════════════════════════════════════════════
// Hata zarfı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tüm hataların gövdesi. Arayüz `code` üzerinden dallanır; `message` yalnızca
 * gösterim içindir — metin değişince davranış bozulmasın.
 *
 * `fieldErrors` yalnız doğrulama hatasında dolu; `ZodValidationPipe` zaten
 * bu biçimi üretiyor (ilk hata kazanır).
 */
export interface ApiErrorBody {
  code?: string;
  message: string;
  fieldErrors?: Record<string, string>;
  /// Bazı kodlar ek bağlam taşır: `INSUFFICIENT_CREDITS` → `{required, available}`,
  /// `RATE_LIMITED` → `{retryAfterSeconds}`, `AD_ACCOUNT_LIMIT` → `{limit, used}`.
  details?: Record<string, unknown>;
}

/// Alan-üstü ortak kodlar. Alanların kendi kodları `<Alan>ErrorCode` içinde;
/// `ErrorCode` gibi genel bir ad KULLANILMAZ, iki alan onu iddia eder.
export const CommonErrorCode = {
  /// Ne başlık ne çerez var. HTTP 400.
  WORKSPACE_REQUIRED: 'WORKSPACE_REQUIRED',
  /// Workspace yok ya da kullanıcı üye değil — ikisi de aynı yanıt. HTTP 404.
  WORKSPACE_NOT_FOUND: 'WORKSPACE_NOT_FOUND',
  /// Üye ama rolü yetmiyor. HTTP 403 — burada 403 doğru: kaynağın varlığı
  /// zaten biliniyor, gizlenecek bir şey yok.
  ROLE_FORBIDDEN: 'ROLE_FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  /// Hız sınırı. `details.retryAfterSeconds` dolu.
  RATE_LIMITED: 'RATE_LIMITED',
  /// Aynı `Idempotency-Key` farklı bir gövdeyle geldi — tehlikeli, reddedilir.
  IDEMPOTENCY_KEY_REUSED: 'IDEMPOTENCY_KEY_REUSED',
  /// Aynı anahtarla gönderilen istek hâlâ işleniyor. HTTP 409.
  IDEMPOTENCY_IN_PROGRESS: 'IDEMPOTENCY_IN_PROGRESS',
  /// Sağlayıcı (İyzico / Anthropic / video) yapılandırılmamış. HTTP 503.
  /// Arayüz beyaz ekran değil "henüz açık değil" gösterir.
  NOT_CONFIGURED: 'NOT_CONFIGURED',
  /**
   * Çalışma alanının HİÇ planı yok — henüz bir plan seçilmemiş. HTTP 402.
   *
   * `SUBSCRIPTION_MISSING`ten (billing) farkı kapsam: o, tek bir eylemin
   * hakkını sorgulayan `EntitlementService`in cevabı. Bu ise KAPININ kendisi:
   * plansız çalışma alanında kiracı kapsamlı hiçbir uç açılmıyor.
   *
   * 🔴 Yalnız "hiç plan seçilmemiş" durumu. Süresi DOLMUŞ abonelikte bu kod
   * dönmüyor — orada kullanıcının verisi var ve §7 "veri rehin alınmaz"
   * diyor: pano, raporlar ve dışa aktarma açık kalır, yalnız yazma yüzeyleri
   * `EntitlementService` tarafından reddedilir.
   */
  PLAN_REQUIRED: 'PLAN_REQUIRED',
} as const;
export type CommonErrorCode = (typeof CommonErrorCode)[keyof typeof CommonErrorCode];

// ═══════════════════════════════════════════════════════════════════════════
// Rol yetki matrisi — TEK KAYNAK
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Yetenek listesi. `EK6` ekranı (rol yetki tablosu) bunu BASAR, kendi
 * listesini yazmaz; guard'lar da aynı tabloyu okur. İki yerde tutulursa
 * ekranda yazan ile sunucunun uyguladığı ayrışır ve kullanıcı "yapabilirim"
 * yazan bir şeyde 403 alır.
 */
export const CAPABILITIES = [
  'workspace.read',
  'workspace.update',
  'workspace.delete',
  'member.read',
  'member.invite',
  'member.role_change',
  'member.remove',
  'ownership.transfer',
  'billing.read',
  'billing.manage',
  'connection.read',
  'connection.manage',
  'campaign.read',
  'campaign.write',
  'report.read',
  'report.write',
  'automation.read',
  'automation.write',
  'automation.approve',
  'creative.read',
  'creative.generate',
  'creative.publish',
  'creative.delete',
  /// Şema borç turu — elle moderasyon kararı (FLAGGED / CLEARED / REJECTED).
  /// KARARIN yeteneği; onaya GÖNDERME `creative.generate`in altında kalıyor.
  'creative.moderate',
  'agent.read',
  'agent.write',
  'audit.read',
  'data.export',
] as const;
export type Capability = (typeof CAPABILITIES)[number];

/**
 * Her yetenek için GEREKEN EN DÜŞÜK rol. Karşılaştırma `ROLE_RANK` ile.
 *
 * Rütbe tabanlı olması bilinçli: "ADMIN yapabilir ama OWNER yapamaz" gibi bir
 * durum ürünün hiçbir yerinde yok ve olsaydı da kullanıcıyı şaşırtırdı.
 *
 * ⚠️ Matrise SIĞMAYAN üç kural sunucuda ayrıca uygulanır — tablo bunları
 * ifade edemez, kod etmek zorunda:
 *   1. `member.role_change`: ADMIN yalnız MEMBER/VIEWER atayabilir; OWNER
 *      rolünü yalnız OWNER verir.
 *   2. Son OWNER düşürülemez / çıkarılamaz (`LAST_OWNER`).
 *   3. Kimse kendi rolünü yükseltemez.
 */
export const CAPABILITY_MIN_ROLE: Record<Capability, WorkspaceRole> = {
  'workspace.read': 'VIEWER',
  'workspace.update': 'ADMIN',
  'workspace.delete': 'OWNER',
  'member.read': 'VIEWER',
  'member.invite': 'ADMIN',
  'member.role_change': 'ADMIN',
  'member.remove': 'ADMIN',
  'ownership.transfer': 'OWNER',
  'billing.read': 'ADMIN',
  'billing.manage': 'OWNER',
  'connection.read': 'VIEWER',
  'connection.manage': 'ADMIN',
  'campaign.read': 'VIEWER',
  'campaign.write': 'MEMBER',
  'report.read': 'VIEWER',
  'report.write': 'MEMBER',
  'automation.read': 'VIEWER',
  'automation.write': 'ADMIN',
  'automation.approve': 'ADMIN',
  'creative.read': 'VIEWER',
  'creative.generate': 'MEMBER',
  'creative.publish': 'MEMBER',
  'creative.delete': 'MEMBER',
  /// ADMIN — `automation.approve` ile aynı rütbe ve aynı gerekçe: bir içeriği
  /// "marka ihlali" diye işaretlemek, üreteni bağlayan bir karar. MEMBER
  /// olsaydı üretici kendi işini temize çekebilirdi.
  'creative.moderate': 'ADMIN',
  'agent.read': 'VIEWER',
  'agent.write': 'MEMBER',
  'audit.read': 'ADMIN',
  'data.export': 'OWNER',
};

export function roleCan(role: WorkspaceRole, capability: Capability): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[CAPABILITY_MIN_ROLE[capability]];
}

/// `GET /workspaces/roles` yanıtı — ekranda basılacak matrisin kendisi.
export interface RoleMatrixResponse {
  roles: WorkspaceRole[];
  capabilities: {
    key: Capability;
    minRole: WorkspaceRole;
    /// Hangi roller yapabilir (önceden hesaplanmış; arayüz karşılaştırma yazmasın).
    allowedRoles: WorkspaceRole[];
  }[];
  /// Matrise sığmayan kuralların i18n anahtarları — ekranda dipnot olarak basılır.
  notes: string[];
}

// ═══════════════════════════════════════════════════════════════════════════
// Denetim kaydı — kiracı tarafı sözlüğü
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `AuditLog.action` değerlerinin TEK kaynağı (kiracı tarafı).
 *
 * `ADMIN_AUDIT_ACTION` yalnız `admin.` önekli platform eylemlerini taşıyor;
 * kiracının kendi eylemleri öneksiz. Dize olarak elle yazılırsa `/admin/audit`
 * ve `/dashboard/settings/security` ekranları satırların bir kısmını tanımaz
 * ve yazım farkı GEÇMİŞE DÖNÜK düzeltilemez.
 *
 * Yazma noktası tek: `AuditService.record(...)`. Dağıtık
 * `prisma.auditLog.create()` yasak.
 */
export const AUDIT_ACTION = {
  WORKSPACE_CREATED: 'workspace.created',
  WORKSPACE_UPDATED: 'workspace.updated',
  WORKSPACE_DELETED: 'workspace.deleted',
  OWNERSHIP_TRANSFERRED: 'workspace.ownership_transferred',

  MEMBER_INVITED: 'member.invited',
  MEMBER_INVITE_RESENT: 'member.invite_resent',
  MEMBER_INVITE_REVOKED: 'member.invite_revoked',
  MEMBER_JOINED: 'member.joined',
  MEMBER_ROLE_CHANGED: 'member.role_changed',
  MEMBER_REMOVED: 'member.removed',
  MEMBER_LEFT: 'member.left',

  CONNECTION_CREATED: 'connection.created',
  CONNECTION_RECONNECTED: 'connection.reconnected',
  CONNECTION_REMOVED: 'connection.removed',
  /// Jeton yenilenemedi; sistem bağlantıyı kopuk işaretledi (aktör null).
  CONNECTION_LOST: 'connection.lost',
  ACCOUNT_CONNECTED: 'account.connected',
  ACCOUNT_SYNC_REQUESTED: 'account.sync_requested',
  /// Platform Operasyon Kataloğu (29 Eyl 2026) — `@mentorivo/shared/platform-ops`.
  /// `after.opId` hangi operasyon olduğunu, `after.origin` kimin tetiklediğini
  /// (agent | operator | api | automation | user) taşıyor.
  PLATFORM_OP_APPLIED: 'platform_op.applied',
  PLATFORM_OP_FAILED: 'platform_op.failed',
  ACCOUNT_SYNC_TOGGLED: 'account.sync_toggled',

  CAMPAIGN_STATUS_CHANGED: 'campaign.status_changed',
  BUDGET_UPDATED: 'budget.updated',

  /**
   * Faz 5 — RMF. "Kim oluşturdu" ile "kim yayına aldı" AYRI iki soru ve ayrı
   * iki satır: oluşturulan her şey `PAUSED` doğuyor, yayına alma ayrı bir
   * `CAMPAIGN_STATUS_CHANGED` satırı yazıyor. Tek bir "kampanya açıldı"
   * satırı, para harcamaya kimin başlattığını kaydın dışında bırakırdı.
   */
  CAMPAIGN_CREATED: 'campaign.created',
  ADSET_CREATED: 'adset.created',
  AD_CREATED: 'ad.created',

  REPORT_EXPORTED: 'report.exported',

  WORKFLOW_CREATED: 'workflow.created',
  WORKFLOW_UPDATED: 'workflow.updated',
  WORKFLOW_ENABLED: 'workflow.enabled',
  WORKFLOW_DISABLED: 'workflow.disabled',
  WORKFLOW_DELETED: 'workflow.deleted',
  ACTION_APPROVED: 'workflow.action_approved',
  ACTION_REJECTED: 'workflow.action_rejected',
  ACTION_ROLLED_BACK: 'workflow.action_rolled_back',

  BILLING_CHECKOUT_STARTED: 'billing.checkout_started',
  BILLING_PLAN_CHANGED: 'billing.plan_changed',
  /// Dönem sonunda iptal işaretlendi / geri alındı (22 Eyl 2026). Plan
  /// değişmediği için `BILLING_PLAN_CHANGED` değil — o kayıt "planınız X
  /// oldu" bildirimi doğuruyor ve iptalde yanlış cümle olurdu.
  BILLING_SUBSCRIPTION_CANCELED: 'billing.subscription_canceled',
  BILLING_SUBSCRIPTION_RESUMED: 'billing.subscription_resumed',
  /// Dönem sonuna planlanmış plan değişikliği (düşürme) ya da geri alınması.
  BILLING_PLAN_CHANGE_SCHEDULED: 'billing.plan_change_scheduled',
  BILLING_GRACE_STARTED: 'billing.grace_started',
  BILLING_GRACE_ENDED: 'billing.grace_ended',
  TOPUP_PURCHASED: 'billing.topup_purchased',

  CREATIVE_GENERATED: 'creative.generated',
  CREATIVE_DELETED: 'creative.deleted',
  CREATIVE_PUBLISHED: 'creative.published',

  // ── Şema borç turu — kreatif onayı ve içerik moderasyonu ────────────────
  /// Kreatif ekip içi onaya GÖNDERİLDİ. Kararın kendisi ayrı kaydediliyor
  /// (`ACTION_APPROVED` / `ACTION_REJECTED`, mevcut onay kuyruğunun uçları):
  /// talep ile karar iki farklı kişinin iki farklı eylemi.
  CREATIVE_APPROVAL_REQUESTED: 'creative.approval_requested',
  /// 🔴 `ActionExecutorService`in başarı denetimi bu değeri yazıyor —
  /// `BUDGET_UPDATED` DEĞİL. Onaylanan bir görsel için "bütçe güncellendi"
  /// diyen bir denetim satırı, denetimin kendisini işe yaramaz yapardı.
  CREATIVE_APPROVED: 'creative.approved',
  CREATIVE_REJECTED: 'creative.rejected',
  /// Elle verilen moderasyon kararı. Sağlayıcının otomatik reddi denetime
  /// GİRMİYOR: onu insan yapmadı, denetim kaydı ise "kim yaptı"nın defteri.
  /// Otomatik kararın yeri `moderation_verdicts` tablosu.
  CREATIVE_MODERATED: 'creative.moderated',

  AGENT_PROPOSAL_APPROVED: 'agent.proposal_approved',
  AGENT_PROPOSAL_REJECTED: 'agent.proposal_rejected',
  /// Faz 8 — SOHBET KARTINDAN İNSAN EYLEMİ (`POST /agent/cards/:id/actions/:a`).
  /// `after`: sohbet, kart, eylem, tür, hedef (opId/tool), durum. Platform
  /// işleminin kendisi ayrıca `PLATFORM_OP_APPLIED` (origin `user`) yazar.
  AGENT_CARD_ACTION: 'agent.card_action',

  // ── Faz 4 — ajan kimliği ────────────────────────────────────────────────
  AGENT_CREATED: 'agent.created',
  AGENT_UPDATED: 'agent.updated',
  /// 🔴 Ayrı kaydediliyor, `AGENT_UPDATED`in içinde değil: "bu kural neden
  /// çalışmadı" sorusunun denetim kaydında doğrudan bir cevabı olmalı.
  /// Ajanı kapatmak altındaki BÜTÜN kuralları susturur ve bu, tek bir alan
  /// güncellemesinden çok daha büyük bir olay.
  AGENT_ENABLED: 'agent.enabled',
  AGENT_DISABLED: 'agent.disabled',
  AGENT_DELETED: 'agent.deleted',
  /// Kural bir ajana bağlandı ya da bağı koptu.
  AGENT_WORKFLOWS_ASSIGNED: 'agent.workflows_assigned',

  NOTIFICATION_PREFS_UPDATED: 'notification.preferences_updated',

  PROFILE_UPDATED: 'user.profile_updated',
  /// 🔴 TARİHÎ — parola 10 Eki 2026'da kalktı, bu eylem artık YAZILMIYOR.
  /// Sabit duruyor çünkü eski denetim satırları onu taşıyor ve panel o
  /// satırları okunur bir etiketle göstermeli.
  PASSWORD_CHANGED: 'user.password_changed',
  EMAIL_CHANGE_REQUESTED: 'user.email_change_requested',
  SESSION_REVOKED: 'user.session_revoked',
  SESSIONS_REVOKED_OTHERS: 'user.sessions_revoked_others',
  DATA_EXPORTED: 'user.data_exported',
  ACCOUNT_DELETED: 'user.account_deleted',

  // ── Genişleme Adım 1 — mağaza ───────────────────────────────────────────
  //
  // 🔴 `CONNECTION_*` yeniden kullanılmadı ve bu bilinçli: `/admin/audit` ve
  // güvenlik ekranı satırları eyleme göre gruplandırıyor, ve "reklam hesabı
  // bağlandı" ile "mağaza bağlandı" farklı sorular. Aynı değeri paylaşsalardı
  // "hangi Meta hesabı bağlandı" filtresi mağaza satırlarını da getirirdi.
  STORE_CONNECTED: 'store.connected',
  STORE_DISCONNECTED: 'store.disconnected',
  STORE_SYNC_REQUESTED: 'store.sync_requested',
  STORE_SYNC_TOGGLED: 'store.sync_toggled',

  // ── Genişleme Adım 9 — ticari operasyon ─────────────────────────────────
  //
  // 🔴 Fatura profili değişimi denetleniyor ve `PROFILE_UPDATED` ile
  // BİRLEŞTİRİLMEDİ: o kişinin kendi profili, bu çalışma alanının hukuki
  // kimliği. "TCKN'i kim değiştirdi" sorusunun, kullanıcı adı değişiklikleri
  // arasında kaybolmayan bir cevabı olmalı.
  //
  // ⚠️ Denetim kaydına kimlik numarasının KENDİSİ yazılmıyor — `before`/`after`
  // yalnız hangi alanların değiştiğini taşıyor. Aksi hâlde maskeleme kararı
  // `audit_logs` üzerinden delinirdi.
  BILLING_PROFILE_UPDATED: 'billing.profile_updated',
  /// Tahsilat kaydı oluştu. Ödemenin kendisi sağlayıcıda; bu satır "bizim
  /// tarafta ne zaman görüldü"nün kaydı.
  PAYMENT_RECORDED: 'billing.payment_recorded',
  PAYMENT_REFUNDED: 'billing.payment_refunded',
  /// Chargeback işaretlendi/kaldırıldı — `/admin`den ELLE.
  PAYMENT_DISPUTED: 'billing.payment_disputed',
  /// Tahsilat denemesi reddedildi (yenileme ya da kullanıcı ödemesi).
  /// `payments`e yazılmıyor (orada yalnız tahsil edilen para var); izi
  /// burada ve `payment_attempts`ta. `billing.payment_failed` bildirimini
  /// bu kayıt doğuruyor.
  PAYMENT_FAILED: 'billing.payment_failed',
  /// Kayıtlı kart eklendi/silindi. Kart jetonu kayda YAZILMAZ — yalnız son
  /// dört hane ve marka.
  PAYMENT_CARD_ADDED: 'billing.card_added',
  PAYMENT_CARD_REMOVED: 'billing.card_removed',
  INVOICE_ISSUED: 'billing.invoice_issued',
  /// Hesap yöneticisinin rezervasyon adresine gidildi. Toplantı SATIRI
  /// yazılmıyor (yöneticinin takvimini göremiyoruz); tıklamanın kaydı
  /// "kaç kişi randevu almaya çalıştı" sorusunun tek dürüst cevabı.
  MEETING_BOOKING_OPENED: 'sales.meeting_booking_opened',

  // ── Geliştirici API'si — 18 Eyl 2026 ────────────────────────────────────
  //
  // Anahtar, dışarıdaki bir programa (MCP istemcisi, yapay zekâ ajanı, kendi
  // betiğin) bu çalışma alanında işlem yapma yetkisi veriyor. "Kim, ne
  // zaman, hangi kapsamla yetki verdi" sorusunun tek dürüst cevabı bu iki
  // satır. Ham anahtar denetime YAZILMIYOR — yalnız adı, kapsamı ve öneki.
  API_KEY_CREATED: 'apikey.created',
  API_KEY_REVOKED: 'apikey.revoked',

  // ── Müşterinin MCP sunucuları — 1 Eki 2026 ─────────────────────────────
  //
  // Bağlantı, yapay zekâya DIŞ bir sistemde (CRM, belge deposu) araç
  // çağırma yetkisi veriyor ve o çağrılar bizim onay kartımızdan geçmiyor;
  // "kim hangi aracı açtı" sorusunun cevabı bu satırlar. Jeton/sır YAZILMAZ —
  // yalnız ad, adres, yetkilendirme türü ve izin listesi.
  MCP_CONNECTOR_CREATED: 'mcp.connector_created',
  MCP_CONNECTOR_UPDATED: 'mcp.connector_updated',
  MCP_CONNECTOR_DELETED: 'mcp.connector_deleted',
  MCP_CONNECTOR_AUTHORIZED: 'mcp.connector_authorized',

  // ── Reklam videosu prodüksiyonu — Kreatif stüdyo Faz 2, 1 Eki 2026 ──────
  //
  // Yalnız KREDİ hareket ettiren dört an denetleniyor: başlatma (storyboard
  // rezervi), onay (üretimin en kötü durumu), revizyon rızası (ek rezerv) ve
  // iptal (o ana kadarki gerçek maliyet kesilir). Cevap, konsept seçimi,
  // senaryo/çekim düzenlemesi yazılmıyor — kullanıcının saniyesi önce kendi
  // ekranında yaptığı iş, `PROFILE_UPDATED` ile aynı sınıf (bildirim
  // köprüsünün dışarıda bırakma gerekçesi). `after.credits` her satırda.
  PRODUCTION_STARTED: 'production.started',
  PRODUCTION_APPROVED: 'production.approved',
  PRODUCTION_REVISION_CONFIRMED: 'production.revision_confirmed',
  PRODUCTION_CANCELED: 'production.canceled',
  /// Taslaktan ÖNCE AI zarfını aşan biçimin ek kredi rızası (Dalga 5) — ayrı
  /// rezerv açılıyor (`production:<id>:plan:<n>`), kredi hareket ediyor.
  PRODUCTION_PLAN_CONSENTED: 'production.plan_consented',
  /// SAĞLAYICI KURTARMASI (3 Eki 2026) — bizim sağlayıcı hesabımız yüzünden
  /// FAILED olmuş prodüksiyon beklemeye geri alındı: kullanıcının önceki
  /// rızasıyla (`min(güncel en kötü durum, rıza)`) çekim rezervi YENİDEN açıldı.
  /// Kredi hareket ediyor; `after` rıza, güncel fiyat ve ayrılan tutarı taşır.
  PRODUCTION_PROVIDER_RESCUED: 'production.provider_rescued',

  // ── Marka kiti + marka hafızası — Dalga 5, 2 Eki 2026 ──────────────────
  //
  // Kit müşterinin bütün reklamlarına giren beyan (ton, renk, logo, son kart);
  // "kim değiştirdi" sorusunun cevabı bu satır. Not ekleme/silme de yazılıyor:
  // AI'ın bir sonraki prodüksiyonda neye uyacağını değiştiriyor ve sohbet
  // ajanının (AGENT) yazdığı notun izi kullanıcının kendisininkinden ayrılmalı
  // (`after.source`). Not metni denetime girer (≤ 500), kit alanları `after`ta.
  BRAND_KIT_UPDATED: 'brand.kit_updated',
  BRAND_MEMORY_NOTE_CREATED: 'brand.memory_note_created',
  BRAND_MEMORY_NOTE_UPDATED: 'brand.memory_note_updated',
  BRAND_MEMORY_NOTE_DELETED: 'brand.memory_note_deleted',
} as const;
export type AuditAction = (typeof AUDIT_ACTION)[keyof typeof AUDIT_ACTION];

export const AUDIT_TARGET_TYPE = {
  WORKSPACE: 'workspace',
  MEMBER: 'member',
  INVITATION: 'invitation',
  CONNECTION: 'connection',
  AD_ACCOUNT: 'ad_account',
  CAMPAIGN: 'campaign',
  ADSET: 'adset',
  AD: 'ad',
  WORKFLOW: 'workflow',
  WORKFLOW_ACTION: 'workflow_action',
  SUBSCRIPTION: 'subscription',
  CREDIT_BALANCE: 'credit_balance',
  CREATIVE: 'creative',
  CREATIVE_JOB: 'creative_job',
  AGENT_CONVERSATION: 'agent_conversation',
  /// Faz 4 — ajan KİMLİĞİ. `AGENT_CONVERSATION`dan ayrı: biri bir oturum,
  /// diğeri kalıcı bir nesne.
  AGENT: 'agent',
  USER: 'user',
  SESSION: 'session',
  /// Genişleme Adım 1 — mağaza bağlantısı. `CONNECTION`dan AYRI çünkü ayrı
  /// bir tabloya işaret ediyor (`store_connections`) ve denetim ekranı
  /// `targetId`den kayda gidebilmek için hangi tabloya bakacağını bilmeli.
  STORE_CONNECTION: 'store_connection',
  /// Genişleme Adım 9. `SUBSCRIPTION`dan ayrı: ayrı tablolara işaret
  /// ediyorlar ve denetim ekranı `targetId`den kayda giderken hangi tabloya
  /// bakacağını bilmek zorunda (`STORE_CONNECTION` ile aynı gerekçe).
  BILLING_PROFILE: 'billing_profile',
  PAYMENT: 'payment',
  INVOICE: 'invoice',
  /// Kayıtlı kart (`payment_cards`) — 22 Eyl 2026.
  PAYMENT_CARD: 'payment_card',
  DEMO_REQUEST: 'demo_request',
  ACCOUNT_MANAGER: 'account_manager',
  API_KEY: 'api_key',
  /// Müşterinin MCP sunucusu (`mcp_connectors`) — 1 Eki 2026.
  MCP_CONNECTOR: 'mcp_connector',
  /// Reklam videosu prodüksiyonu (`creative_productions`) — 1 Eki 2026.
  /// `CREATIVE`ten ayrı: hedef bir kreatif değil, teslimleri birden çok
  /// kreatif olan bir süreç (`STORE_CONNECTION` ile aynı gerekçe).
  CREATIVE_PRODUCTION: 'creative_production',
  /// Marka kiti (`brand_kits`, çalışma alanı başına tek) ve marka hafızası notu
  /// (`brand_memory_notes`) — Dalga 5.
  BRAND_KIT: 'brand_kit',
  BRAND_MEMORY_NOTE: 'brand_memory_note',
} as const;
export type AuditTargetType = (typeof AUDIT_TARGET_TYPE)[keyof typeof AUDIT_TARGET_TYPE];

/// Denetim listesi satırı (workspace bazlı okuma — GV3/GV4).
export interface AuditRow {
  id: string;
  action: string;
  targetType: string | null;
  targetId: string | null;
  actorUserId: string | null;
  actorEmail: string | null;
  actorName: string | null;
  ip: string | null;
  userAgent: string | null;
  /// Öncesi/sonrası — satır detayında açılır. Sır alanları temizlenmiş hâlde.
  before: unknown | null;
  after: unknown | null;
  createdAt: string;
}

export const auditListQuerySchema = listQuerySchema.extend({
  actorUserId: z.uuid().optional(),
  action: z.string().max(80).optional(),
  targetType: z.string().max(40).optional(),
  targetId: z.string().max(64).optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});
export type AuditListQuery = z.infer<typeof auditListQuerySchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Tarih aralığı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Göreli aralık kısayolları. Kaydedilmiş raporlar SABİT TARİH DEĞİL bunu
 * saklar — "son 7 gün" diye kaydedilen rapor bir ay sonra açıldığında yine
 * son 7 günü göstermeli.
 */
export const relativeRangeSchema = z.enum([
  'today',
  'yesterday',
  'last_7d',
  'last_14d',
  'last_30d',
  'last_90d',
  'this_month',
  'last_month',
  'this_year',
  'custom',
]);
export type RelativeRange = z.infer<typeof relativeRangeSchema>;

/// Tek gün (`YYYY-MM-DD`). Saat taşımaz: `MetricDaily.date` bir `@db.Date`.
export const daySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Tarih YYYY-MM-DD olmalı');

/**
 * En büyük sorgulanabilir aralık. Aşımda 400 `RANGE_TOO_LARGE`.
 *
 * 400 gün seçildi ki "geçen yılın aynı dönemi" karşılaştırması (365 + tolerans)
 * tek sorguda yapılabilsin, ama biri `from=2000-01-01` yazıp tabloyu taramasın.
 */
export const MAX_RANGE_DAYS = 400;

export const dateRangeSchema = z
  .object({
    /// Dahil. Workspace zaman diliminde gün.
    from: daySchema,
    /// Dahil.
    to: daySchema,
  })
  .refine((v) => v.from <= v.to, { message: 'Başlangıç bitişten sonra olamaz', path: ['from'] });
export type DateRange = z.infer<typeof dateRangeSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Kuyruk adları — API üretir, worker tüketir
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kuyruk adları burada, çünkü iki AYRI uygulama (apps/api ve apps/worker)
 * aynı dizeyi kullanmak zorunda. Bir tarafta yazım hatası yaparsanız iş
 * kuyruğa girer ve KİMSE ALMAZ — hata da vermez, sadece hiç olmaz.
 *
 * ⚠️ Worker bugün PM2'de TANIMLI DEĞİL (ecosystem.config.js yalnız web+api).
 * Bu kuyruklara düşen hiçbir iş bu turda İŞLENMEYECEK. Üretici taraf yine de
 * doğru yazılır; eksik olan tek şey süreci ayağa kaldırmak.
 */
export const QUEUE = {
  /// Platform senkronu: hiyerarşi + metrik çekme (integrations).
  SYNC: 'sync',
  /// Kural koşusu (automation).
  AUTOMATION: 'automation',
  /**
   * Tek bir aksiyonun platforma uygulanması (automation).
   *
   * ⚠️ Faz 4'ten sonra bu kuyruk MİRAS: tüketicisi worker'da değil, worker
   * onu `AUTOMATION_WRITE`e AKTARIYOR (`apps/worker/.../action.processor.ts`).
   * Hâlâ ÜRETİLİYOR — hem worker'ın koşu servisi hem API'nin ajan öneri
   * servisi buraya yazıyor — ve Redis'te bekleyen eski işler de buradan
   * boşaltılıyor. Yeni üretici taraf yazma, `AUTOMATION_WRITE` kullan.
   */
  AUTOMATION_ACTION: 'automation-action',
  /**
   * 🔴 PLATFORMA YAZMA — TÜKETİCİSİ `apps/api`, worker DEĞİL (Faz 4).
   *
   * ═══════════════════════════════════════════════════════════════════════
   * Neden ters yön
   * ═══════════════════════════════════════════════════════════════════════
   * Aksiyonu platforma yazan tek doğru yol `ActionExecutorService` →
   * `PlatformActionService` → `ConnectorRegistry` ve o zincir API sürecinde
   * yaşıyor; beş platformu da (META/GOOGLE/TIKTOK/LINKEDIN/X) o biliyor.
   * Worker `apps/api/src`'i IMPORT EDEMEZ (ayrı derleme birimi) ve kendi
   * yazıcısını taşıdığı sürece yalnız Meta'yı biliyordu: Google/TikTok/
   * LinkedIn/X hesabına kurulmuş bir AUTOPILOT kural her gece
   * `Platform henüz desteklenmiyor` ile FAILED oluyordu.
   *
   * REDDEDİLEN ALTERNATİF: dört yazıcıyı daha worker'a kopyalamak. Para
   * hareket ettiren mantığın ikinci bir kopyası demekti ve sapması SESSİZ
   * olurdu — guardrail'in uygulama anındaki ikinci kontrolü, idempotency
   * anahtarı ve `beforeState`/`afterState` yazımı iki yerde yaşardı.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * "API'de `@Processor` YOK" kuralını ÇİĞNEMİYOR
   * ═══════════════════════════════════════════════════════════════════════
   * O kuralın gerekçesi şuydu: *"İki süreçte aynı kuyruğa işlemci kaydetmek
   * işleri ARALARINDA BÖLÜŞTÜRÜRDÜ."* Bu kuyruğun işlemcisi YALNIZ API'de
   * kayıtlı — bölüşecek ikinci tüketici yok. Emsali `MESSAGING`: aynı
   * gerekçe, aynı yön.
   */
  AUTOMATION_WRITE: 'automation-write',
  /// Kreatif render (creative).
  RENDER: 'render',
  /// Rapor/CSV/PDF üretimi ve GDPR arşivi (analytics + workspaces).
  EXPORT: 'export',
  /// E-posta ve zamanlanmış bildirim gönderimi (notifications).
  NOTIFY: 'notify',
  /**
   * 🔴 BİLDİRİM DAĞITIMI — TÜKETİCİSİ `apps/api`, worker DEĞİL (9 Eyl 2026).
   *
   * ═══════════════════════════════════════════════════════════════════════
   * Neden ters yön — `AUTOMATION_WRITE` ile BİREBİR aynı gerekçe
   * ═══════════════════════════════════════════════════════════════════════
   * Bildirimi yedi kanala dağıtan zincir API sürecinde yaşıyor:
   * `NotificationService` → `NotificationDispatchService` → yedi kanal
   * sürücüsü. Sürücülerden ikisi (Slack, WhatsApp) `MessagingService`e,
   * biri (WhatsApp) QR ile açılmış uzun ömürlü bir WebSocket'e bağlı ve o
   * soket API sürecinde.
   *
   * Worker `apps/api/src`'i IMPORT EDEMEZ. Kendi yazıcısını taşıdığı sürece
   * yalnız İKİ kanalı biliyordu (uygulama içi + e-posta) ve maliyeti üç ayrı
   * dosyada AÇIKÇA yazılıydı:
   *
   *   `render-notify.ts`      → *"KANAL TERCİHİ ve E-POSTA GÖNDERİMİ
   *                              ATLANIYOR"*
   *   `maintenance/notify.ts` → *"kanal tercihi ve e-posta gönderimi
   *                              ATLANIYOR"*
   *
   * Yani 20 dakikalık bir video render'ı bitiyor, kullanıcı SMS istemiş
   * olsa bile yalnız zilde bir satır görüyordu.
   *
   * REDDEDİLEN ALTERNATİF: yedi sürücüyü, kanal kayıt defterini, şablon
   * çözümünü ve dil zincirini worker'a kopyalamak. `AUTOMATION_WRITE`
   * kararının aynısı: ikinci kopya SESSİZCE sapardı — bir tarafta
   * güncellenen bir şablon, diğer tarafta eski metinle gönderilirdi.
   *
   * ⚠️ Üç dosyanın da eski notu "kalıcı çözüm kuyruğa atmak" diyordu ve
   * engel olarak *"o kuyruğu tüketen worker PM2'de tanımlı DEĞİL"*
   * gösteriliyordu. O engel geçersiz (worker ayakta, 12 Ağu 2026'da
   * doğrulandı) ve bu kuyruğun tüketicisi zaten worker değil API.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * "API'de `@Processor` YOK" kuralını ÇİĞNEMİYOR
   * ═══════════════════════════════════════════════════════════════════════
   * O kuralın gerekçesi *"iki süreçte aynı kuyruğa işlemci kaydetmek işleri
   * ARALARINDA BÖLÜŞTÜRÜRDÜ"*. Bu kuyruğun işlemcisi YALNIZ API'de kayıtlı —
   * bölüşecek ikinci tüketici yok. Emsalleri `MESSAGING` ve
   * `AUTOMATION_WRITE`.
   *
   * ⚠️ BEDELİ: API düşükse bildirimler Redis'te BEKLER. Kayıp değil,
   * gecikme — ve API düşükse ürünün kendisi zaten erişilemez durumda.
   */
  NOTIFY_DISPATCH: 'notify-dispatch',
  /**
   * 🔴 FATURALAMA TURU — TÜKETİCİSİ `apps/api`, worker DEĞİL (22 Eyl 2026).
   *
   * Worker on dakikada bir buraya TEK iş atıyor; API'deki işlemci yenileme,
   * uzlaştırma ve süre aşımı turunu koşuyor (`BillingRenewalService.tick`).
   * Ters yön `AUTOMATION_WRITE`/`NOTIFY_DISPATCH` ile aynı gerekçe: para
   * çeken mantık (iyzico istemcisi, tutar, deneme durum makinesi, teslim)
   * API'de ve ikinci kopyası yasak. Zamanlama worker'da, mantık tek yerde.
   *
   * API düşükse iş Redis'te BEKLER — yenileme gecikir, kaybolmaz.
   */
  BILLING_TICK: 'billing-tick',
  /**
   * OTONOM OPERATÖR koşuları (29 Eyl 2026, OTONOM-OPERATOR-PLANI.md §1.C).
   *
   * 🔴 TÜKETİCİSİ `apps/api` — LLM döngüsü, ajan araçları ve POK yürütücüsü
   * API'de; worker `apps/api`yi import edemiyor. Worker yalnız ZAMANLIYOR
   * (vadesi gelen görev, devriye) ve iş atıyor; API ayrıca olaylardan
   * (kampanya yayına alındı, performans alarmı) kendisi atıyor.
   * `jobId` deterministik (`operator-<runId>`): aynı koşu iki kez atılmaz.
   */
  AGENT_OPERATOR: 'agent-operator',
  /**
   * İŞLEM PLANI yürütmesi (Faz 5, 29 Eyl 2026) — onaylanan `platform_op_plan`
   * kartı ve 3 adımdan uzun REST planı (`POST /platform-ops/run-plan`).
   *
   * 🔴 TÜKETİCİSİ `apps/api` (`PlatformOpPlanProcessor`): adımlar
   * `PlatformOpsService.run` ile koşuyor ve o yürütücü API'de. Onay isteği
   * dakikalar süren bir planı (medya yükleme, TikTok doğum kapısı, Meta
   * yayını) BEKLEMİYOR — kart `applying` olur, iş adım adım ilerleyip kartı
   * günceller. Yeniden deneme güvenli: adımların idempotency anahtarı
   * `proposal:<id>:<adım>` / `http:<anahtar>:<adım>` ve uygulanmış adım kayıtta.
   */
  PLATFORM_OP_PLAN: 'platform-op-plan',
  /// Zamanlanmış bakım işleri: kredi dönem devri, onay süre aşımı,
  /// yumuşak sınır kontrolü, oturum temizliği (billing + automation).
  MAINTENANCE: 'maintenance',
  /**
   * WhatsApp gönderimi (Faz 3).
   *
   * 🔴 TÜKETİCİSİ `apps/api` — diğer altı kuyruğun TERSİ.
   *
   * Sebep mimari bir zorunluluk: WhatsApp bağlantısı QR ile açılan, uzun
   * ömürlü TEK bir WebSocket ve o soket API sürecinde yaşıyor (QR'ı tarayıcıya
   * ulaştıran uç de orada). Worker'ın zamanlanmış kuralı `notify_external`
   * uyguladığında o sokete ULAŞAMAZ.
   *
   * ⚠️ Bu, `automation.module.ts`'teki *"API tarafında hiçbir `@Processor`
   * YOK"* kuralının İSTİSNASI ve kuralın GEREKÇESİNİ çiğnemiyor. O kural
   * şunu diyordu: *"İki süreçte aynı kuyruğa işlemci kaydetmek işleri
   * ARALARINDA BÖLÜŞTÜRÜRDÜ."* Buranın işlemcisi YALNIZ API'de kayıtlı —
   * bölüşecek ikinci bir tüketici yok. Worker bu kuyruğa yalnız ÜRETİCİ
   * olarak dokunuyor.
   *
   * Slack bu kuyruktan GEÇMİYOR: incoming webhook durumsuz bir HTTPS
   * isteği, her iki süreç de doğrudan gönderebiliyor.
   */
  MESSAGING: 'messaging',
  /**
   * Mağaza senkronu: katalog + sipariş + stok (Genişleme Adım 1).
   *
   * 🔴 `SYNC`ten AYRI kuyruk, aynı kuyruğa iş atmıyor. Sebep işletme:
   * reklam senkronu 15 dakikada bir ve DAR (hesap başına birkaç yüz satır);
   * mağaza senkronu ilk koşuda 90 günlük sipariş geçmişi, yani on binlerce
   * satır sürebilen UZUN bir iş. Aynı kuyruğa koysaydık tek bir büyük
   * mağazanın ilk senkronu, bütün kiracıların pano tazeliğini bloklardı —
   * BullMQ eşzamanlılığı kuyruk başına ayarlanıyor.
   *
   * Tüketicisi YALNIZ worker (`@Processor` API'de yok kuralı).
   */
  STORE_SYNC: 'store-sync',
  /**
   * Rakip analizi: Ad Library tazelemesi + AI içgörü raporu (Adım 10).
   *
   * 🔴 `SYNC`ten AYRI ve sebebi ÖLÇÜLMÜŞ bir kota gerçeği: Meta Ad Library
   * kotası UYGULAMA seviyesinde, kiracı seviyesinde değil. 14 Ağu 2026'da
   * `ads_archive` uygulama jetonuyla çağrıldığında dönen hata
   * (`code 10 / error_subcode 2332004`) uygulamanın kendisine ait; jeton da
   * platforma ait tek bir jeton (`AdLibraryConfig`). Yani bütün kiracılar
   * TEK BİR kotayı paylaşıyor ve eşzamanlılığın bağımsız kısılması gerekiyor.
   *
   * `SYNC` kuyruğuna koysaydık iki kötü seçenekten birini seçmek zorunda
   * kalırdık: reklam senkronunu da Ad Library hızına yavaşlatmak, ya da
   * kotayı yakıp bütün kiracıların rakip verisini birden düşürmek.
   *
   * İçgörü raporu da burada: aynı verinin üstünde çalışıyor ve LLM çağrısı
   * zaten seyrek (kullanıcı tetikliyor, zamanlanmış değil).
   *
   * Tüketicisi YALNIZ worker.
   */
  COMPETITOR: 'competitor',
  /**
   * ÜRÜN GÖRSELİ ANALİZİ — vision LLM çağrısı (20 Ağu 2026).
   *
   * 🔴 `RENDER`DAN AYRI KUYRUK ve gerekçesi ölçülebilir bir gecikme:
   * `RENDER_QUEUE_CONCURRENCY` 2 ve bir ürün videosu 20 dakikaya kadar
   * sürebiliyor (`RENDER_TIMEOUT_SECONDS.video`). Analizi oraya koysaydık,
   * iki video üretilirken fotoğraf yükleyen kullanıcı analiz sonucunu kırk
   * dakika bekleyebilirdi — oysa işin kendisi 10–25 saniye.
   *
   * `COMPETITOR`a da konmadı: o kuyruk `concurrency: 1` ve o sayı Ad
   * Library'nin UYGULAMA seviyesindeki kotasından geliyor. Buranın böyle bir
   * kotası yok (Anthropic sınırı hesap başına ve çok daha geniş), yani aynı
   * kuyruğu paylaşmak analizi gereksiz yere seri hâle getirirdi.
   *
   * Tüketicisi YALNIZ worker.
   */
  VISION: 'vision',
  /**
   * 🔴 KURAL OLAYI — TÜKETİCİSİ `apps/api`, worker DEĞİL (Faz 7b, 30 Eyl 2026).
   *
   * Worker'ın operatör gözcüsü (`operator-watch.service.ts`) `ad.disapproved`,
   * `lead.received` ve `campaign.launched`ı tespit ediyor; aynı tespit kural
   * olayını da buraya bırakıyor. Olayın TEK dağıtıcısı API'de
   * (`WorkflowEventsService`: susturma, hedef daraltma, süzgeç, koşu) — worker'a
   * ikinci bir dağıtıcı yazmak susturmayı ve hedef daraltmayı iki yerde
   * yaşatırdı. `AUTOMATION_WRITE` / `NOTIFY_DISPATCH` ile aynı ters yön.
   * Yük: `WorkflowEventJobPayload` (`@mentorivo/automation-core`).
   */
  AUTOMATION_EVENT: 'automation-event',
  /**
   * 🔴 REKLAM YORUMU YOKLAMASI — TÜKETİCİSİ `apps/api` (Faz 7b, 30 Eyl 2026).
   *
   * Tazelik zamanlayıcısı (worker `live/freshness.scheduler.ts`) "vakti geldi
   * mi" sorusunu soruyor ve hesap başına iş bırakıyor; yorumu OKUYAN
   * `PlatformOpsService` (beş platformun `<p>.comment.list` işleyicisi,
   * jeton kasası, sahiplik) API'de. Bağlayıcının worker'a kopyası yazılmadı.
   * Hesap başına EN FAZLA BİR bekleyen iş (`live:pending:comments:*`).
   */
  COMMENT_WATCH: 'comment-watch',
  /**
   * PRODÜKSİYON (reklam videosu motoru) — Kreatif stüdyo Faz 2, 1 Eki 2026.
   *
   * 🔴 ÜÇ KUYRUK, `RENDER`dan AYRI ve gerekçe iş profilinin üç ayrı şekli
   * (KREATIF-STUDYO-PLANI.md §2.5–2.6):
   *
   *   `PRODUCTION`       orkestrasyon + LLM adımları (anla, konsept, senaryo,
   *                      çekim listesi, QA yargıcı çağrıları). Kısa, ağ
   *                      bekleyen işler — eşzamanlılık 4.
   *   `PRODUCTION_GEN`   fal üretimleri + yoklama (anahtar kare, take, müzik,
   *                      dış ses). Bir video take'i 12 sn ile 7 dk arası
   *                      sağlayıcıda BEKLİYOR, CPU harcamıyor — eşzamanlılık
   *                      12. Render'ın 2'lik eşzamanlılığını TÜKETMEZ: bir
   *                      prodüksiyon tek seferde 20+ take gönderiyor ve
   *                      render kuyruğunu paylaşsaydı tezgâhtaki tek görsel
   *                      dakikalarca beklerdi.
   *   `PRODUCTION_MEDIA` ffmpeg (kurgu, miks, son kart, çıktılar). CPU ve
   *                      RAM ağır — 10 sn 1080×1920 x264 ≈ 8 sn, ~1 GB RSS,
   *                      %370 CPU (ölçüldü) — eşzamanlılık 1.
   *
   * Tek uzun BullMQ işi DEĞİL: durum DB'de (`creative_productions`), her
   * aşama kısa ve idempotent bir iş, bitince `PRODUCTION_ADVANCE` kuyruklar.
   * Worker `nest --watch` altında — kaynak değişikliği uçuştaki işi öldürür;
   * iş kimlikleri deterministik (`productionJobId`) ve fal isteği gönderim
   * anında yazılı, yani yeniden deneme kaldığı yerden sürer.
   *
   * Tüketicisi YALNIZ worker.
   */
  PRODUCTION: 'production',
  PRODUCTION_GEN: 'production-gen',
  PRODUCTION_MEDIA: 'production-media',
} as const;
export type QueueName = (typeof QUEUE)[keyof typeof QUEUE];

/// İş adları. Kuyruk adı gibi bunlar da iki uygulamada ortak.
export const JOB = {
  SYNC_ACCOUNT: 'sync.account',
  SYNC_BACKFILL: 'sync.backfill',
  AUTOMATION_RUN: 'automation.run',
  AUTOMATION_APPLY: 'automation.apply',
  /**
   * Faz 4 — aksiyonu platforma yazma isteği (`QUEUE.AUTOMATION_WRITE`).
   *
   * `AUTOMATION_APPLY`den AYRI bir iş adı ve bu bilinçli: iki ad aynı olsaydı
   * miras kuyruğu boşaltan aktarıcı (`action.processor.ts`) kendi ürettiği
   * işi kendi kuyruğunda görüp göremediğini iş adından ayırt edemezdi ve
   * `/admin/queues` ekranında iki yol tek satıra karışırdı.
   *
   * Yük ŞEKLİ aynı: `AutomationActionJob` (`@mentorivo/automation-core`) —
   * yalnız `{ workspaceId, actionLogId }`. Hedef değeri yükte TAŞINMAZ,
   * uygulayıcı satırı veritabanından taze okur (gerekçe o tipin başında).
   */
  AUTOMATION_WRITE: 'automation.write',
  RENDER_CREATIVE: 'render.creative',
  /**
   * ÖZEL MANKEN REFERANS FOTOĞRAFI — `QUEUE.RENDER` (21 Ağu 2026).
   *
   * ═══════════════════════════════════════════════════════════════════════
   * Neden AYNI kuyruk, neden AYRI iş adı
   * ═══════════════════════════════════════════════════════════════════════
   * Aynı kuyruk: yaptığı iş bir fal görsel üretimi ve o kuyruğun
   * eşzamanlılığı (2) tam olarak fal'ın bellek/ağ profiline göre ayarlanmış.
   * İkinci bir kuyruk açmak, aynı sağlayıcıya aynı anda dört iş göndermek
   * demekti — ayarın anlamı kalmazdı.
   *
   * Ayrı iş adı: yükü `RenderJobPayload` DEĞİL (ortada `Creative` satırı yok,
   * `StudioModel` satırı var) ve işlemci iş adına bakarak ayırıyor. Tek adla
   * gönderilseydi ayrım yükün şekline bakmakla yapılırdı — yani sessizce
   * yanlış dala düşen bir iş, kredisi rezerve edilmiş hâlde kaybolurdu.
   */
  RENDER_STUDIO_MODEL: 'render.studio_model',
  EXPORT_CSV: 'export.csv',
  EXPORT_PDF: 'export.pdf',
  EXPORT_WORKSPACE_DATA: 'export.workspace_data',
  NOTIFY_EMAIL: 'notify.email',
  /// 9 Eyl 2026 — worker'dan API'ye bildirim dağıtım isteği
  /// (`QUEUE.NOTIFY_DISPATCH`). Yük `NotifyInput`un kendisi.
  NOTIFY_DISPATCH: 'notify.dispatch',
  /// Faturalama turu (`QUEUE.BILLING_TICK`). Yük boş — tur "şimdi"yi kendisi okur.
  BILLING_TICK: 'billing.tick',
  /// Operatör koşusu (`QUEUE.AGENT_OPERATOR`). Yük `{ workspaceId, runId }` —
  /// tetik ve bağlam `operator_runs` satırında.
  OPERATOR_RUN: 'operator.run',
  /// İşlem planı kartı (`QUEUE.PLATFORM_OP_PLAN`). Yük `{ workspaceId, proposalId, … }`.
  PLATFORM_OP_PLAN_PROPOSAL: 'platform_op_plan.proposal',
  /// REST işlem planı (`QUEUE.PLATFORM_OP_PLAN`). Yük `{ workspaceId, planId }`.
  PLATFORM_OP_PLAN_HTTP: 'platform_op_plan.http',
  MAINT_CREDIT_ROLLOVER: 'maint.credit_rollover',
  MAINT_APPROVAL_TIMEOUT: 'maint.approval_timeout',
  MAINT_SOFT_LIMIT: 'maint.soft_limit',
  MAINT_SESSION_CLEANUP: 'maint.session_cleanup',
  /// Faz 3 — worker'dan API'ye WhatsApp gönderim isteği (`QUEUE.MESSAGING`).
  MESSAGING_WHATSAPP: 'messaging.whatsapp',
  /// Genişleme Adım 1 — mağaza senkronu (`QUEUE.STORE_SYNC`).
  SYNC_STORE: 'store.sync',
  /// Genişleme Adım 10 — rakip analizi (`QUEUE.COMPETITOR`).
  COMPETITOR_SYNC: 'competitor.sync',
  COMPETITOR_INSIGHT: 'competitor.insight',
  /// Ürün fotoğrafı analizi → video planı (`QUEUE.VISION`), 20 Ağu 2026.
  VISION_PRODUCT: 'vision.product',
  /// Faz 7b — worker'ın tespit ettiği kural olayı (`QUEUE.AUTOMATION_EVENT`).
  AUTOMATION_EVENT: 'automation.event',
  /// Faz 7b — hesap başına yorum yoklaması (`QUEUE.COMMENT_WATCH`).
  COMMENT_POLL: 'comment.poll',
  // ── Prodüksiyon (Kreatif stüdyo Faz 2) — yük `{ workspaceId, productionId, … }` ──
  /// `QUEUE.PRODUCTION` — durum makinesini bir adım ilerlet: saf
  /// `nextActions(...)` hesaplanır, çıkan işler kuyruklanır. Her aşama işi
  /// bitince bunu kuyruklar; süpürücü takılan prodüksiyonu bununla dürter.
  PRODUCTION_ADVANCE: 'production.advance',
  /// `QUEUE.PRODUCTION` — LLM aşaması (anla, sorular, konsept, senaryo,
  /// çekim listesi, revizyon yönlendiricisi); hangisi yükte `stage`.
  PRODUCTION_STEP: 'production.step',
  /// `QUEUE.PRODUCTION_GEN` — referans seti: arka plan silme (+ yazısız
  /// üründe QA'dan geçen açı görünümü).
  PRODUCTION_REFS: 'production.refs',
  /// `QUEUE.PRODUCTION_GEN` — çekimin anahtar kare adayları.
  PRODUCTION_KEYFRAME: 'production.keyframe',
  /// `QUEUE.PRODUCTION_GEN` — tek take'in gönderimi + yoklaması.
  PRODUCTION_TAKE: 'production.take',
  /// `QUEUE.PRODUCTION` — QA yargıcı (take ya da anahtar kare); kare ızgarası
  /// `PRODUCTION_MEDIA`da hazırlanmış olmalı.
  PRODUCTION_QA: 'production.qa',
  /// `QUEUE.PRODUCTION_GEN` — müzik, dış ses, SFX, STT denetimleri.
  PRODUCTION_AUDIO: 'production.audio',
  /// `QUEUE.PRODUCTION_MEDIA` — kurgu: kırpma, müziğe kesme, geçiş, renk eşleme.
  PRODUCTION_EDIT: 'production.edit',
  /// `QUEUE.PRODUCTION_MEDIA` — miks, son kart, ek boyutlar, kesimler,
  /// altyazı, kapak.
  PRODUCTION_FINISH: 'production.finish',
  /// `QUEUE.PRODUCTION` — teslim: `Creative` satırları (`AD_VIDEO`,
  /// `productionId` + `rendition`) ve bildirim.
  PRODUCTION_DELIVER: 'production.deliver',
  /// `QUEUE.PRODUCTION` — aşama rezervini gerçek maliyetle kapat (`settle`).
  PRODUCTION_SETTLE: 'production.settle',
} as const;
export type JobName = (typeof JOB)[keyof typeof JOB];

// ═══════════════════════════════════════════════════════════════════════════
// Olay adları — EventEmitter üzerinden alanlar arası gevşek bağ
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `EventEmitterModule` app.module'de kurulu. Bildirim üreten alanlar
 * NotificationService'i doğrudan da çağırabilir; olay yolu, üreten alanın
 * bildirim modülüne DERLEME BAĞIMLILIĞI olmasın diye var.
 */
export const EVENT = {
  CONNECTION_DISCONNECTED: 'connection.disconnected',
  CONNECTION_RECOVERED: 'connection.recovered',
  SYNC_FAILED: 'sync.failed',
  WORKFLOW_APPROVAL_PENDING: 'workflow.approval.pending',
  WORKFLOW_ACTION_APPLIED: 'workflow.action.applied',
  WORKFLOW_ACTION_FAILED: 'workflow.action.failed',
  CREDIT_LOW: 'credit.low',
  CREATIVE_JOB_SUCCEEDED: 'creative.job.succeeded',
  CREATIVE_JOB_FAILED: 'creative.job.failed',
  BILLING_GRACE_STARTED: 'billing.grace.started',
  BILLING_GRACE_ENDED: 'billing.grace.ended',
  /**
   * "Sahibinden iste" (1 Eki 2026) — ödeme yetkisi olmayan üye abonelik
   * şeridinden sahibe hatırlatıyor. Yayıncı `BillingAlertService` (alan
   * başına günde bir), dinleyici `notification.listeners.ts` → yalnız OWNER.
   * Olay yolu: faturalama modülü bildirim modülünü import etmesin.
   */
  BILLING_RENEWAL_REQUESTED: 'billing.renewal.requested',
  INVITATION_SENT: 'invitation.sent',

  /**
   * HESAP GÜVENLİĞİ — 16 Eyl 2026.
   *
   * 🔴 Bu üçü için olay yolu ZORUNLU, tercih değil.
   *
   * Yayıncı `AuthService` ve o, `NotificationService`i DOĞRUDAN alamıyor:
   * `NotificationsModule` guard'ı için `AuthModule`ü import ediyor, ters
   * yönde bir import modül döngüsü doğurur. Aynı duvara 10 Eyl 2026'da
   * sistem postalarında da çarpıldı ve çözüm orada bir port olmuştu
   * (`mail/system-mail.port.ts`). Burada port gerekmiyor: bildirim
   * "gönderildi mi" cevabı beklemiyor, yayınlanıp unutuluyor.
   */
  ACCOUNT_EMAIL_CHANGED: 'account.email.changed',
  ACCOUNT_LOGIN_NEW_DEVICE: 'account.login.new_device',
  /// Art arda yanlış giriş kodu — EŞİĞİ AŞTIĞINDA yayınlanıyor, her
  /// denemede değil (gerekçe `notifications.ts` → `account.login_failed`).
  ACCOUNT_LOGIN_FAILED: 'account.login.failed',

  /**
   * DENETİM KAYDI YAZILDI — 16 Eyl 2026, bildirim köprüsünün tetiği.
   *
   * `AuditService` her satırdan sonra bunu yayınlıyor; `notifications` alanı
   * `AUDIT_NOTIFICATIONS` haritasına bakıp karşılığı varsa bildirim üretiyor.
   * Denetim servisi bildirim modülünü TANIMIYOR — 78 eylemin bildirim
   * karşılığı tek bir haritada, otuz ayrı `notify()` çağrısında değil.
   */
  AUDIT_RECORDED: 'audit.recorded',

  /**
   * PANEL GÜVENLİK OLAYI (backstage). Kiracı denetiminin ikizi ama ayrı
   * akış: orada olayın bir çalışma alanı var, burada YOK — olay bir KİŞİNİN
   * başına geliyor (parolası denendi, 2FA'sı sıfırlandı) ve köprü o kişinin
   * birincil alanına yazıyor.
   */
  BACKSTAGE_SECURITY: 'backstage.security',
} as const;
export type EventName = (typeof EVENT)[keyof typeof EVENT];

// ═══════════════════════════════════════════════════════════════════════════
// Gerçek zamanlılık ve demo veri — iki bağlayıcı karar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Yoklama aralıkları — canlı akışın YEDEĞİ.
 *
 * 22 Eyl 2026: veri değişimi artık SSE ile itiliyor (`GET /live/stream`,
 * sözleşme `live.ts`): izlenen alan dakikada bir tazeleniyor ve worker yazdığı
 * anda panel haber alıyor; zil de yeni bildirimde anında güncelleniyor. Bu
 * aralıklar yalnız akışın taşımadığı şeyler (iş ilerlemesi: render, elle
 * senkron, dışa aktarma) ve akış koptuğunda zilin yedeği için.
 *
 * Önerilen aralıklar (arayüz bunlara uyar, kendi sayısını uydurmaz):
 */
export const POLL_INTERVAL_MS = {
  /// Bildirim sayacı — kabuk her sayfada çalıştırıyor, hafif uç.
  notificationCount: 60_000,
  /// Devam eden kreatif/dışa aktarma işi varken.
  jobProgress: 3_000,
  /// Senkron sürerken bağlantı ekranı.
  syncStatus: 10_000,
} as const;

/**
 * API ASLA demo veri üretmez.
 *
 * `/dashboard` bugün `apps/web/src/lib/demo.ts` ile çalışıyor ve GB10 gereği
 * "bu sayılar uydurma" etiketi taşıyor. Gerçek uçlar geldiğinde demo tamamen
 * ARAYÜZDE kalır ve etiketli olur; API bağlantı yokken `state: 'no_connection'`
 * döner. Sunucunun uydurma sayı döndürmesi, kullanıcının bir gün onlara
 * inanması demektir.
 */
export const API_NEVER_RETURNS_DEMO_DATA = true;

// ═══════════════════════════════════════════════════════════════════════════
// Prompt şablonu motoru
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `{{yerTutucu}}` çözümlemesi — video ve banner katalogları ORTAK kullanıyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Boş değerde yer tutucu SATIRDAN SİLİNİR, boş dizeye çevrilmez
 * ═══════════════════════════════════════════════════════════════════════════
 * Bu kural Adım 7'de ÖLÇÜLMÜŞ bir hata sınıfından doğdu: şablonlar yer
 * tutucuyu cümlenin İÇİNDE kullanıyor ve `The mood is {{brandTone}}.` boş
 * değerde `The mood is.` bırakıyor. Model o kalıntıyı bir yönerge sanıyor —
 * hata yok, log yok, yalnız kötü bir çıktı ve harcanmış kredi. Bu yüzden
 * çözümden sonra fazla boşluklar tekleniyor ve noktalama öncesi boşluk
 * kaldırılıyor.
 *
 * ---------------------------------------------------------------------------
 * Neden burada, `video.ts`te değil
 * ---------------------------------------------------------------------------
 * Adım 8'in banner kataloğu aynı kuralı istiyor ve iki kopya hâlinde
 * yazılsaydı biri düzeltildiğinde diğeri eski kalırdı — Adım 1'de dört
 * Shopify okuyucusunun aynı yardımcıyı üç farklı gövdeyle yazması tam olarak
 * bu şekilde olmuştu. `common.ts` nötr yer: iki katalog da onu import ediyor,
 * ikisi birbirini import etmiyor.
 *
 * Bilinmeyen yer tutucu OLDUĞU GİBİ bırakılıyor (silinmiyor): katalog
 * şemaları onu zaten reddediyor ve buradaki sessiz silme, o doğrulamayı
 * atlatan bir yolda hatayı görünmez yapardı.
 */
export function renderPromptPlaceholders<K extends string>(
  template: string,
  values: Partial<Record<K, string | null>>,
  allowed: readonly K[],
): string {
  return template
    .replace(/\{\{(\w+)\}\}/g, (whole, key: string) => {
      if (!allowed.includes(key as K)) return whole;
      return values[key as K]?.trim() ?? '';
    })
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([.,;:])/g, '$1')
    .trim();
}
